import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';
// FLARUM 2.0: another extension's module needs the `ext:` prefix. In
// flarum-webpack-config v3 a bare `flarum/...` import ALWAYS resolves to the
// `core` namespace, so 'flarum/markdown/common/components/MarkdownButton'
// compiled to flarum.reg.get('core', 'markdown/common/components/MarkdownButton'),
// which is undefined. The build does not catch it, because externals resolve at
// runtime. The toolbar hook then threw after removing the stock link button,
// leaving no link button and the stock image button. Found in a browser (A4).
import MarkdownButton from 'ext:flarum/markdown/common/components/MarkdownButton';
import PostStreamState from 'flarum/forum/states/PostStreamState';
import IndexPage from 'flarum/forum/components/IndexPage';
// FLARUM 2.0: byobu's nav item moved off IndexPage onto IndexSidebar.
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import LinkButton from 'flarum/common/components/LinkButton';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Tooltip from 'flarum/common/components/Tooltip';
import Link from 'flarum/common/components/Link';
// FLARUM 2.0: the `avatar` helper is gone; it is a component now.
import Avatar from 'flarum/common/components/Avatar';
import humanTime from 'flarum/common/utils/humanTime';
import UrlInsertModal from './components/UrlInsertModal';
const modifierKey = navigator.userAgent.match(/Macintosh/) ? '⌘' : 'ctrl';
function tooltip(name, hotkey) {
  return app.translator.trans(`flarum-markdown.lib.composer.${name}_tooltip`) + (hotkey ? ` <${modifierKey}-${hotkey}>` : '');
}
function openModal(kind, editor) {
  const [start, end] = editor.getSelectionRange();
  const selectedText = editor.el.value.slice(start, end);
  const looksLikeUrl = /^https?:\/\//i.test(selectedText.trim());
  app.modal.show(UrlInsertModal, {
    kind,
    initialUrl: looksLikeUrl ? selectedText.trim() : '',
    initialText: looksLikeUrl ? '' : selectedText,
    onsubmit: markdown => {
      editor.insertBetween(start, end, markdown);
    }
  });
}
app.initializers.add('fixedgr-ux-tweaks', () => {
  if (!TextEditor.prototype.markdownToolbarItems) return;
  extend(TextEditor.prototype, 'markdownToolbarItems', function (items) {
    const composer = this.attrs.composer;
    if (!composer || !composer.editor) return;
    const editor = composer.editor;
    if (items.has('link')) {
      items.remove('link');
      items.add('link', m(MarkdownButton, {
        title: tooltip('link'),
        icon: "fas fa-link",
        onclick: () => openModal('link', editor)
      }), 300);
    }
    if (items.has('image')) {
      items.remove('image');
      items.add('image', m(MarkdownButton, {
        title: tooltip('image'),
        icon: "fas fa-image",
        onclick: () => openModal('image', editor)
      }), 200);
    }
  });
});

// ---- Composer focus: the text editor, not "Add Poll" -----------------------
//
// Composer.focus() focuses the first match of
// '.Composer-content :input:enabled:visible, .TextEditor-editor'. fof/polls
// adds "Add Poll" to the composer header, ahead of the editor. On 1.8
// (fof/polls 2.3.3) it was an <a> with no href, which isn't an :input, so focus
// fell through to the editor. FLARUM 2.0: fof/polls 3.0.0-rc.4 renders it with
// core's Button, a real <button>, so it took focus on every reply and Space or
// Enter opened the poll modal (reported 2026-10-20, d2306 #4541).
//
// Core's own hook for this is the static ComposerBody.focusOnSelector, which
// DiscussionComposer uses to focus its title. Point replies and edits at the editor.
//
// FLARUM 2.0: ReplyComposer and EditPostComposer are lazy chunks, loaded by
// app.composer.load(() => import(...)) on the first reply or edit. A static
// import resolves to flarum.reg.get() at boot, which is undefined, so the
// initializer threw ("failed to initialize", shown to admins only). The
// registry's onLoad runs the handler when the chunk registers the module (or
// at once if it is already loaded).
const focusEditor = module => {
  const ComposerBodyClass = module && module.default || module;
  if (ComposerBodyClass) ComposerBodyClass.focusOnSelector = () => '.TextEditor-editor';
};
app.initializers.add('fixedgr-ux-tweaks-composer-focus', () => {
  flarum.reg.onLoad('core', 'forum/components/ReplyComposer', focusEditor);
  flarum.reg.onLoad('core', 'forum/components/EditPostComposer', focusEditor);
});

// ---- Post-stream refresh ---------------------------------------------------
//
// Counterpart to fast-discussion's FastPostCreate, which suppresses the
// discussion's `posts` relationship linkage on the reply response for threads
// over the threshold. Without that linkage the cached discussion's postIds()
// never learns about the new post, so update() short-circuits on viewingEnd()
// and the reply the user just wrote never appears.
//
// On 1.8 this compensated for FastCreatePostController dropping
// `discussion.posts`. The mechanism in 2.0 is different — a relationship
// linkage rather than an attribute — but the symptom and the fix are
// identical.
//
// FLARUM 2.0 VERIFIED: PostStreamState.update() is unchanged in shape:
//
//     update() {
//       if (!this.viewingEnd()) return Promise.resolve();
//       return this.syncEnd();
//     }
//
// so the override still applies cleanly.
//
// Detect the case: any Post in the store belonging to this discussion but
// absent from postIds() means a post was created (or arrived over Pusher)
// without the discussion being refreshed. Fetch fresh postIds, then continue
// with the original update() flow.
app.initializers.add('fixedgr-ux-tweaks-stream-refresh', () => {
  override(PostStreamState.prototype, 'update', function (original) {
    const disc = this.discussion;
    if (!disc) return original.call(this);
    const postIds = disc.postIds && disc.postIds() || [];
    const postIdSet = new Set(postIds.map(String));

    // Store.all<M>(type) is still public in 2.0 (common/Store.ts).
    const stalePosts = app.store.all('posts').filter(p => {
      const d = p.discussion && p.discussion();
      if (!d || String(d.id()) !== String(disc.id())) return false;
      return !postIdSet.has(String(p.id()));
    });
    if (stalePosts.length === 0) {
      return original.call(this);
    }
    const latestNumber = Math.max(...stalePosts.map(p => p.number() || 0));
    return app.store.find('discussions', String(disc.id()), {
      bySlug: true,
      page: {
        near: latestNumber
      }
    }).then(() => original.call(this)).catch(() => original.call(this));
  });
});

// ---- Private-message unread badge -----------------------------------------
//
// byobu adds a "Private Discussions" nav item but never surfaces an unread
// indicator, so a DM is invisible until the recipient clicks through. We append
// a count badge sourced from `is:private is:unread`.
//
// byobu declares `unreadPrivateMessagesCount` on the User model but its PHP
// side never populates it, so we do not rely on it.
//
// FLARUM 2.0: byobu moved this nav item from IndexPage.navItems to
// IndexSidebar.prototype.navItems. Verified against fof/byobu 2.0.0-rc.1
// (js/src/forum/pages/PrivateDiscussionsPage.js). The LinkButton.component
// (attrs, children) shape is unchanged, and byobu still keys the item
// 'privateDiscussions'.
const PM_FETCH_THROTTLE_MS = 10_000;
let unreadPmCount = 0;
let lastPmFetchAt = 0;
function refreshUnreadPmCount(force) {
  if (!app.session || !app.session.user) {
    unreadPmCount = 0;
    return;
  }
  const now = Date.now();
  if (!force && now - lastPmFetchAt < PM_FETCH_THROTTLE_MS) return;
  lastPmFetchAt = now;

  // FLARUM 2.0: send the DISCRETE filters. 2.0 moved gambit parsing to the
  // client: `is:private is:unread` becomes `filter[private]=true` plus
  // `filter[unread]=true`, and the server no longer understands the gambit
  // string. Sent raw as `filter[q]=is:private is:unread`, the server runs a
  // FULL-TEXT SEARCH for the words "private" and "unread" across 2.7M posts:
  // a wrong count (942 for a user with 0 unread) at ~3 s of DB time per index
  // visit. That was the port's first version, and it saturated the 1-vCPU
  // database under load (task A6, 2026-09-22).
  //
  // Verified against a SQL ground truth for six users, including three with
  // "mark all as read" set: the discrete form matched exactly, in 0.04-0.7 s.
  // The zero it returned during the port was CORRECT, not broken.
  //
  // We use app.request rather than store.find so we can ask for one row and
  // read meta.page.total instead of counting a capped page.
  app.request({
    method: 'GET',
    url: app.forum.attribute('apiUrl') + '/discussions',
    params: {
      filter: {
        private: 'true',
        unread: 'true'
      },
      page: {
        limit: 1
      }
    }
  }).then(res => {
    const total = res && res.meta && res.meta.page ? res.meta.page.total : null;
    unreadPmCount = typeof total === 'number' ? total : res && res.data ? res.data.length : 0;
    m.redraw();
  }).catch(() => {});
}

// ---- Discussion-list avatar: show last poster's avatar ---------------------
//
// Core's avatar always uses discussion.user() (the OP) while the line beneath
// says "USERNAME replied N min ago" under the default sort. The mismatch is
// jarring. When showFirstPost() is false, show lastPostedUser instead.
//
// FLARUM 2.0: DiscussionListItem.authorAvatarView() no longer exists. The
// avatar is now an ItemList entry keyed 'avatar' inside authorItems(), which
// is a better hook — we replace one item instead of overriding a whole render
// method. Note the CSS class also changed: 'DiscussionListItem-author-avatar'
// (2.0) vs 'DiscussionListItem-author' (1.8).
app.initializers.add('fixedgr-ux-tweaks-last-poster-avatar', () => {
  extend(DiscussionListItem.prototype, 'authorItems', function (items) {
    if (this.showFirstPost()) return;
    if (!items.has('avatar')) return;
    const discussion = this.attrs.discussion;
    const lastUser = discussion.lastPostedUser && discussion.lastPostedUser();
    if (!lastUser) return;
    items.setContent('avatar', m(Tooltip, {
      text: app.translator.trans('core.forum.discussion_list.replied_text', {
        user: lastUser,
        ago: humanTime(discussion.lastPostedAt())
      }),
      position: "right"
    }, m(Link, {
      className: "DiscussionListItem-author-avatar",
      href: app.route.user(lastUser)
    }, m(Avatar, {
      user: lastUser,
      title: ""
    }))));
  });
});
app.initializers.add('fixedgr-ux-tweaks-pm-badge', () => {
  // app.session isn't hydrated during the initializer phase — defer past boot.
  setTimeout(() => refreshUnreadPmCount(true), 0);
  extend(IndexPage.prototype, 'oninit', function () {
    refreshUnreadPmCount();
  });
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (!items.has('privateDiscussions') || unreadPmCount <= 0) return;
    const label = unreadPmCount >= 50 ? '50+' : String(unreadPmCount);
    items.setContent('privateDiscussions', LinkButton.component({
      icon: app.forum.data.attributes['byobu.icon-badge'],
      href: app.route('byobuPrivate')
    }, [app.translator.trans('fof-byobu.forum.nav.nav_item'), m("span", {
      className: "fixedgr-PmUnreadBadge"
    }, label)]));
  });
});